import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C } from "../theme/colors";
import { NEWS_ITEMS } from "../data/constants";
import Divider from "../components/Divider";
import Section from "../components/Section";
import PageHero from "../components/PageHero";

const EVENTS_API_URL = "https://ccapi.rapifuzz.in:8443/api/events/";
const API_BASE = "https://ccapi.rapifuzz.in:8443";

function formatEventDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]       = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [hasNext, setHasNext]         = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Debounce the search box so we don't fire a request on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set("page", currentPage);
    if (search) params.set("search", search);

    fetch(`${EVENTS_API_URL}?${params.toString()}`)
      .then(res => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data.results || []);
        setEvents(list);
        setTotalPages(data.total_pages || 1);
        setHasNext(!!data.has_next);
        setHasPrevious(!!data.has_previous);
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentPage, search]);

  return (
    <div className="page-fade">
      <PageHero
        tag="Events"
        title="Events & Conclaves"
        subtitle="Symposiums, workshops, and outreach programmes bringing together researchers, industry, and defence stakeholders."
      />

      <Section>
        <div style={{textAlign:"center",marginBottom:40}}>
          <span className="section-tag">What's Coming Up</span>
          <h2 style={{fontWeight:600,color:C.navyDark,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",marginTop:10}}>Upcoming Events</h2>
          <div style={{display:"flex",justifyContent:"center"}}>
            <Divider/>
          </div>
          <p className="body" style={{maxWidth:560,margin:"0 auto"}}>Save the date for these upcoming CRENS events.</p>
        </div>

        <div style={{maxWidth:420,margin:"0 auto 32px"}}>
          <div style={{position:"relative"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="2" style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)"}}>
              <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search events…"
              style={{width:"100%",padding:"11px 14px 11px 40px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:14,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
            />
          </div>
        </div>

        {loading && (
          <p className="body" style={{textAlign:"center"}}>Loading events…</p>
        )}

        {!loading && error && (
          <p className="body" style={{textAlign:"center",color:C.textMuted}}>
            Couldn't load events right now. Please check back shortly.
          </p>
        )}

        {!loading && !error && events.length === 0 && (
          <p className="body" style={{textAlign:"center",color:C.textMuted}}>
            No upcoming events at the moment — check back soon.
          </p>
        )}

        {!loading && !error && events.length > 0 && (
          <>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:20}}>
              {events.map((ev,i) => (
                <div
                  key={ev.id ?? i}
                  className="card"
                  onClick={() => {
                    if (ev.id == null) return;
                    navigate(`/events/${ev.id}`);
                  }}
                  style={{padding:0,overflow:"hidden",cursor: ev.id != null ? "pointer" : "default"}}
                >
                  {ev.image && (
                    <img
                      src={`${API_BASE}${ev.image}`}
                      alt={ev.title}
                      style={{width:"100%",height:200,objectFit:"cover",display:"block"}}
                    />
                  )}
                  <div style={{padding:"20px 24px 24px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      {ev.publish_date && (
                        <span className="tag">{formatEventDate(ev.publish_date)}</span>
                      )}
                      {ev.tags_name && (
                        <span style={{fontSize:13,color:C.textMuted}}>{ev.tags_name}</span>
                      )}
                    </div>
                    <div style={{fontWeight:600,fontSize:16,color:C.navyDark,marginBottom:8,lineHeight:1.3}}>{ev.title}</div>
                    <p className="body" style={{fontSize:14,lineHeight:1.75,margin:0}}>{ev.short_desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:16,marginTop:36}}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={!hasPrevious}
                  style={{padding:"8px 16px",borderRadius:6,border:`1px solid ${C.border}`,background:C.white,fontSize:13.5,fontWeight:600,color: hasPrevious ? C.navyDark : C.textMuted,cursor: hasPrevious ? "pointer" : "default",opacity: hasPrevious ? 1 : 0.5}}
                >
                  ← Previous
                </button>
                <span style={{fontSize:13.5,color:C.textMuted}}>Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={!hasNext}
                  style={{padding:"8px 16px",borderRadius:6,border:`1px solid ${C.border}`,background:C.white,fontSize:13.5,fontWeight:600,color: hasNext ? C.navyDark : C.textMuted,cursor: hasNext ? "pointer" : "default",opacity: hasNext ? 1 : 0.5}}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </Section>

      <Section alt>
        <div style={{textAlign:"center",marginBottom:40}}>
          <span className="section-tag">Past Events</span>
          <h2 style={{fontWeight:600,color:C.navyDark,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",marginTop:10}}>Previous Events & Outreach</h2>
          <div style={{display:"flex",justifyContent:"center"}}>
            <Divider/>
          </div>
          <p className="body" style={{maxWidth:560,margin:"0 auto"}}>A look back at CRENS's conclaves, talks, and awareness campaigns.</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:900,margin:"0 auto"}}>
          {NEWS_ITEMS.map((n,i) => (
            <div key={i} className="card" style={{padding:"22px 26px",display:"flex",gap:20,alignItems:"flex-start"}}>
              <span style={{flexShrink:0,fontWeight:700,fontSize:13,color:C.blue,minWidth:78}}>{n.date}</span>
              <div>
                <div style={{fontWeight:600,fontSize:15.5,color:C.navyDark,marginBottom:6,lineHeight:1.35}}>{n.title}</div>
                <p className="body" style={{fontSize:13.5,lineHeight:1.7,margin:0}}>{n.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}